import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getUsers } from '@/api/user';
import { getWfh, updateWfh } from '@/api/wfh';
import { IGetRequestWFH, IGetWFHResponse, IWfh, IUpdateWFH, IWfhAndUser } from '@/models/wfh.model';
import { UserInterface } from '@/models/user.model';
import { useState } from 'react';
import dayjs from 'dayjs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DatePickerWithRange } from '@/components/ui/date-range-picker';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { DateRange } from 'react-day-picker';
import { Button } from '@/components/ui/button';
import { LogOut } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { WfhStatus } from '@/constants/wfh/wfh.constant';
import { Header } from '@/components/layout/header';
import { useAuthStore } from '@/stores/auth';
import { Main } from '@/components/layout/main';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import toastify from "@/utils/toastify";
import { Label } from "@/components/ui/label";
import TableCustomer from '@/components/table';

const Wfh: React.FC = () => {
    const { logout } = useAuthStore();
    const [selectedUser, setSelectedUser] = useState<string>('all');
    const [selectedStatus, setSelectedStatus] = useState<WfhStatus | 'all'>('all');
    const [dateRange, setDateRange] = useState<DateRange | undefined>({
        from: dayjs().startOf('month').toDate(),
        to: dayjs().endOf('month').toDate()
    });
    const [currentPage, setCurrentPage] = useState(1);
    const pageSize = 10;
    const [selectedWfh, setSelectedWfh] = useState<IWfh | null>(null);
    const [adminNote, setAdminNote] = useState('');
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const queryClient = useQueryClient();

    // Query users list
    const { data: usersData } = useQuery({
        queryKey: ['users'],
        queryFn: () => getUsers({ page: 1, limit: -1 }),
    });

    // Query WFH list with filters
    const { data: wfhData, isLoading } = useQuery<IGetWFHResponse>({
        queryKey: ['wfh', selectedUser, selectedStatus, dateRange, currentPage, pageSize],
        queryFn: () => {
            const params: IGetRequestWFH = {
                startDate: dateRange?.from,
                endDate: dateRange?.to,
                status: selectedStatus === 'all' ? undefined : selectedStatus,
                uid: selectedUser === 'all' ? undefined : selectedUser,
                page: currentPage,
                limit: pageSize
            };
            return getWfh(params);
        },
    });

    // Update WFH mutation
    const updateWfhMutation = useMutation({
        mutationFn: (payload: IUpdateWFH) => updateWfh(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['wfh'] });
            setIsDialogOpen(false);
            setSelectedWfh(null);
            setAdminNote('');
            toastify.success("WFH request updated successfully");
        },
        onError: (error) => {
            toastify.error("Failed to update WFH request");
            console.error('Update WFH error:', error);
        }
    });

    const handleStatusClick = (wfh: IWfh) => {
        if (wfh.status === WfhStatus.Pending) {
            setSelectedWfh(wfh);
            setIsDialogOpen(true);
        }
    };

    const handleSubmit = () => {
        if (!selectedWfh) return;

        const payload: IUpdateWFH = {
            requestId: selectedWfh._id,
            decision: selectedWfh.status,
            note: adminNote,
            originalMessageTs: selectedWfh.originalMessageTs || ''
        };

        updateWfhMutation.mutate(payload);
    };

    const getStatusBadge = (status: WfhStatus) => {
        switch (status) {
            case WfhStatus.Approved:
                return <Badge className="bg-green-500 hover:bg-green-600 text-white">Approved</Badge>;
            case WfhStatus.Rejected:
                return <Badge className="bg-red-500 hover:bg-red-600 text-white">Rejected</Badge>;
            case WfhStatus.Pending:
                return <Badge className="bg-yellow-500 hover:bg-yellow-600 text-white">Pending</Badge>;
            default:
                return <Badge variant="outline">{status}</Badge>;
        }
    };

    const columns = [
        {
            value: 'user.name',
            label: 'Employee Name',
            render: ({ data }: { data: IWfhAndUser }) => data.user.name
        },
        {
            value: 'wfhDay',
            label: 'WFH Date',
            render: ({ data }: { data: IWfhAndUser }) => dayjs(data.wfh.wfhDay).format('DD/MM/YYYY')
        },
        {
            value: 'reason',
            label: 'Reason',
            className: 'max-w-[300px] truncate'
        },
        {
            value: 'status',
            label: 'Status',
            render: ({ data }: { data: IWfhAndUser }) => (
                <div className="flex items-center gap-2">
                    {getStatusBadge(data.wfh.status)}
                    {data.wfh.status === WfhStatus.Pending && (
                        <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2"
                            onClick={() => handleStatusClick(data.wfh)}
                        >
                            Process
                        </Button>
                    )}
                </div>
            )
        }
    ];

    return (
        <>
            <Header fixed className='flex justify-between w-[inherit]'>
                <div className='mr-0 flex items-center justify-end space-x-4 w-[inherit]'>
                    <Button variant="outline" size="icon" onClick={logout}>
                        <LogOut />
                    </Button>
                </div>
            </Header>
            <Main>
                <div className='flex justify-between items-center mb-4'>
                    <h1 className='text-2xl font-bold'>WFH Management</h1>
                </div>

                <Card className="mt-6">
                    <CardHeader>
                        <div className="flex items-center justify-between">
                            <CardTitle>WFH List</CardTitle>
                            <div className="text-sm text-muted-foreground">
                                {isLoading ? (
                                    <Skeleton className="h-4 w-[100px]" />
                                ) : (
                                    `Total: ${wfhData?.total || 0} records`
                                )}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="space-y-2">
                                <Label>Employee</Label>
                                <Select value={selectedUser} onValueChange={setSelectedUser} disabled={isLoading}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select employee" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All</SelectItem>
                                        {usersData?.users.map((user: UserInterface) => (
                                            <SelectItem key={user._id} value={user._id}>
                                                {user.name}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Status</Label>
                                <Select value={selectedStatus} onValueChange={(value: WfhStatus | 'all') => setSelectedStatus(value)} disabled={isLoading}>
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">All</SelectItem>
                                        <SelectItem value={WfhStatus.Pending}>Pending</SelectItem>
                                        <SelectItem value={WfhStatus.Approved}>Approved</SelectItem>
                                        <SelectItem value={WfhStatus.Rejected}>Rejected</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="space-y-2">
                                <Label>Date Range</Label>
                                <DatePickerWithRange
                                    date={dateRange}
                                    setDate={setDateRange}
                                    disabled={isLoading}
                                />
                            </div>
                        </div>
                    </CardContent>
                    <CardContent>
                        <TableCustomer
                            columns={columns}
                            data={wfhData?.data || []}
                            loading={isLoading}
                            pagination={{
                                currentPage,
                                totalPages: Math.ceil((wfhData?.total || 0) / pageSize),
                                totalItems: wfhData?.total || 0,
                                itemsPerPage: pageSize,
                                onPageChange: (page) => setCurrentPage(page)
                            }}
                        />
                    </CardContent>
                </Card>

                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogContent className='bg-white'>
                        <DialogHeader>
                            <DialogTitle>Process WFH Request</DialogTitle>
                            <DialogDescription>
                                Please select the status and provide a note for your decision.
                            </DialogDescription>
                        </DialogHeader>
                        <div className="grid gap-4 py-4">
                            <div className="grid gap-2">
                                <Label>Status</Label>
                                <Select
                                    value={selectedWfh?.status}
                                    onValueChange={(value) => {
                                        if (selectedWfh) {
                                            setSelectedWfh({
                                                ...selectedWfh,
                                                status: value as WfhStatus
                                            });
                                        }
                                    }}
                                >
                                    <SelectTrigger>
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value={WfhStatus.Approved}>Approved</SelectItem>
                                        <SelectItem value={WfhStatus.Rejected}>Rejected</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="grid gap-2">
                                <Label>Note</Label>
                                <Textarea
                                    placeholder="Enter your note here..."
                                    value={adminNote}
                                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setAdminNote(e.target.value)}
                                />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsDialogOpen(false);
                                    setSelectedWfh(null);
                                    setAdminNote('');
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={!adminNote.trim() || !selectedWfh?.status}
                            >
                                Submit
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </Main>
        </>
    );
};

export default Wfh;
